import { useEffect, useState } from "react";
import { X } from "lucide-react";
import type { Locale } from "@/lib/i18n";

const DISMISS_KEY = "shugert_us_site_notice_dismissed_until";
const DISMISS_MS = 1000 * 60 * 60 * 24 * 90;

const COPY = {
  en: {
    text: "Looking for our U.S. Shopify team?",
    link: "Visit Shugert Marketing USA",
    close: "Dismiss U.S. site notice",
  },
  es: {
    text: "¿Buscas nuestro equipo de Shopify en EE. UU.?",
    link: "Visita Shugert Marketing USA",
    close: "Cerrar aviso del sitio de EE. UU.",
  },
} as const;

function dismissedForNow() {
  try {
    const until = Number(window.localStorage.getItem(DISMISS_KEY) ?? "0");
    return Number.isFinite(until) && until > Date.now();
  } catch {
    return false;
  }
}

function cameFromUsSite() {
  try {
    if (!document.referrer) return false;
    return new URL(document.referrer).hostname.replace(/^www\./, "") === "shugertmarketing.com";
  } catch {
    return false;
  }
}

export function USSiteNotice({ locale }: { locale: Locale }) {
  const [visible, setVisible] = useState(false);
  const t = COPY[locale];

  useEffect(() => {
    if (dismissedForNow() || cameFromUsSite()) return;

    const controller = new AbortController();

    void fetch("/api/public/visitor-country", {
      method: "GET",
      credentials: "same-origin",
      cache: "no-store",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) return null;
        return (await response.json()) as { country?: string | null };
      })
      .then((data) => {
        if (data?.country === "US") setVisible(true);
      })
      .catch(() => {
        // Geo detection is optional. Never interrupt the public site if it fails.
      });

    return () => controller.abort();
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS));
    } catch {
      // Storage can be unavailable in strict privacy modes; dismissal still works for this view.
    }
    setVisible(false);
  };

  return (
    <aside
      aria-label={t.text}
      className="fixed right-3 top-[94px] z-40 max-w-[calc(100vw-1.5rem)] rounded-full border border-border/60 bg-background/88 px-3 py-2 shadow-sm backdrop-blur-md md:right-5 md:top-[110px]"
    >
      <div className="flex items-center gap-2 text-[11px] leading-none text-muted-foreground sm:text-xs">
        <span className="hidden sm:inline">{t.text}</span>
        <a
          href="https://shugertmarketing.com/"
          className="font-medium text-foreground underline decoration-border underline-offset-4 transition-opacity hover:opacity-70"
        >
          {t.link} →
        </a>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t.close}
          className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    </aside>
  );
}

export default USSiteNotice;
